"use client";
/**
 * Idle-deferred, non-critical page chrome: the toast host and the cookie banner.
 *
 * PERF: both were statically imported by MarketingLayout, so `sonner` and the banner's
 * deps hydrated on EVERY marketing page even though neither is needed for the initial
 * view — a toast can't fire before the user does something, and consent can be asked a
 * beat later. Loading them via next/dynamic after the browser is idle (or on first
 * interaction) keeps them off the critical path that PageSpeed measures. Same pattern
 * as DeferredChatWidget.
 *
 * Deliberately NOT deferred: SiteHeader/footer (above-the-fold, must render immediately).
 */
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";

const Toaster = dynamic(() => import("@/components/ui/sonner").then((m) => m.Toaster), { ssr: false });
const CookieConsentBanner = dynamic(
  () => import("@/components/ui/CookieConsentBanner").then((m) => m.CookieConsentBanner),
  { ssr: false },
);

export function DeferredChrome() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let done = false;
    const events: (keyof WindowEventMap)[] = ["pointerdown", "keydown", "scroll", "touchstart"];
    const cleanup = () => events.forEach((e) => window.removeEventListener(e, arm));
    function arm() {
      if (done) return;
      done = true;
      setReady(true);
      cleanup();
    }
    events.forEach((e) => window.addEventListener(e, arm, { once: true, passive: true }));
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    if (typeof w.requestIdleCallback === "function") w.requestIdleCallback(arm, { timeout: 2500 });
    else setTimeout(arm, 2000);
    return cleanup;
  }, []);

  if (!ready) return null;
  return (
    <>
      <CookieConsentBanner />
      <Toaster />
    </>
  );
}
